import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  isEditor: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isAdmin: false,
    isEditor: false,
  });

  const checkRoles = useCallback(async (userId: string) => {
    try {
      // Check if user is admin or owner
      const { data: isAdminData } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin',
      });
      
      const { data: isOwnerData } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'owner',
      });
      
      // Check if user is editor or above
      const { data: isEditorData } = await supabase.rpc('is_editor_or_above', {
        _user_id: userId,
      });
      
      setState(prev => ({
        ...prev,
        isAdmin: isAdminData === true || isOwnerData === true,
        isEditor: isEditorData === true,
      }));
    } catch (error) {
      console.error('Error checking roles:', error);
      setState(prev => ({
        ...prev,
        isAdmin: false,
        isEditor: false,
      }));
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
          isLoading: false,
        }));
        
        if (session?.user) {
          // Defer role check to avoid blocking
          setTimeout(() => checkRoles(session.user.id), 0);
        } else {
          setState(prev => ({
            ...prev,
            isAdmin: false,
            isEditor: false,
          }));
        }
      }
    );

    // THEN check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
        isLoading: false,
      }));
      
      if (session?.user) {
        checkRoles(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkRoles]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          display_name: displayName,
        },
      },
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    ...state,
    signIn,
    signUp,
    signOut,
  };
}
